import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getNestAwsSession, invalidateSessionCache } from '../components/header/nestingale_auth';
import { getCustIdFromLS } from './utils/userInfo';

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

type HeadersInit = Record<string, string>;

/**
 * Per-request options you can pass in any axios call config:
 *
 * @example — skip auth headers for a public endpoint:
 *   axiosInstanceApiNestingale.get('/public/data', { skipAuthHeaders: true })
 *
 * @example — add extra headers for a specific call:
 *   axiosInstanceApiNestingale.post('/upload', body, {
 *     extraHeaders: { 'X-Upload-Source': 'web', 'Idempotency-Key': uuid },
 *   })
 */
export interface NestAxiosRequestConfig extends InternalAxiosRequestConfig {
    /** When true, skips injecting Supplier-Id / Designer-Id headers. */
    skipAuthHeaders?: boolean;
    /** Merged onto the request headers for this call only. */
    extraHeaders?: Record<string, string>;
}

const DEFAULT_HEADERS: HeadersInit = {
    'Content-Type': 'application/json',
};

/**
 * Attach auth headers from the cached Cognito session.
 * The cache in nestingale_auth prevents redundant Cognito round-trips.
 */
async function attachAuthHeaders(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
    const req = config as NestAxiosRequestConfig;

    // ── Per-request extra headers (applied regardless of auth) ──────────────
    if (req.extraHeaders) {
        Object.assign(config.headers, req.extraHeaders);
    }

    // ── Auth headers (opt-out with skipAuthHeaders: true) ───────────────────
    if (!req.skipAuthHeaders) {
        try {
            const sessionInfo = await getNestAwsSession();

            if (sessionInfo?.isSessionValid && sessionInfo?.session?.identityId) {
                const { vendorId, identityId } = sessionInfo.session;
                const profile = sessionInfo.session.loginProfile?.toLowerCase();

                if (profile === 'supplier') {
                    config.headers['Supplier-Id'] = vendorId;
                } else if (profile === 'designer') {
                    config.headers['Designer-Id'] = getCustIdFromLS() || identityId;
                }
            }
        } catch (error) {
            console.warn('[axios] Failed to attach identity headers:', error);
        }
    }

    return config;
}

const createAxiosInstance = (baseURL: string, customHeaders: HeadersInit = DEFAULT_HEADERS): AxiosInstance => {
    const instance = axios.create({ baseURL, headers: customHeaders });

    // ── Request interceptor ──────────────────────────────────────────────────
    instance.interceptors.request.use(
        (config) => attachAuthHeaders(config),
        (error) => Promise.reject(error),
    );

    // ── Response interceptor ─────────────────────────────────────────────────
    // On 401, bust the session cache and retry once with fresh credentials.
    instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

            if (error.response?.status === 401 && !originalRequest._retried) {
                originalRequest._retried = true;
                invalidateSessionCache(); // force a fresh Cognito fetch
                const retried = await attachAuthHeaders(originalRequest);
                return instance(retried);
            }

            return Promise.reject(error);
        },
    );

    return instance;
};

// ---------------------------------------------------------------------------
// Pre-configured instances
// ---------------------------------------------------------------------------

/** Plain cloudfront instance — no auth headers */
const axiosObj = axios.create({
    baseURL: 'https://d221a61rb87vel.cloudfront.net/v1',
});

const axiosInstanceNestingale = createAxiosInstance('https://nestingale.com/');
const axiosInstanceApiNestingale = createAxiosInstance('https://api.nestingale.com/');
const aiApiNestingaleToUpload = createAxiosInstance(
    'https://langgraph-chat-app-multiimage-373113822105.us-central1.run.app',
);

export const cloudFrontWithSupplierId = createAxiosInstance("https://zcv0nnbrt4.execute-api.us-east-1.amazonaws.com/dev")

export { axiosInstanceNestingale, axiosInstanceApiNestingale, axiosObj, aiApiNestingaleToUpload };
export default createAxiosInstance;


// example

// import { axiosInstanceApiNestingale } from '@/app/lib/axios';

// No Supplier-Id / Designer-Id will be attached
// const res = await axiosInstanceApiNestingale.get('/public/categories', {
//   skipAuthHeaders: true,
// });


// const res = await axiosInstanceApiNestingale.post('/upload/asset', formData, {
//   extraHeaders: {
//     'X-Upload-Source': 'web',
//     'Idempotency-Key': crypto.randomUUID(),
//   },
// });

// const res = await axiosInstanceApiNestingale.get('/vendor/prices', {
//   skipAuthHeaders: true,
//   extraHeaders: { 'X-Api-Key': 'abc123' },
// });

